import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

/**
 * The AI surface currently in context. One app-wide orb (in AppFooter) is driven by this
 * store: whichever page is active registers its context, and the orb opens the matching
 * drawer. The union is intentionally extensible — new AI surfaces add a `kind` (per the
 * app-wide context-aware orb pattern) without touching the footer/orb wiring.
 */
export type AiChatContext =
  | { kind: 'survey-builder'; surveyId: string }
  | { kind: 'brand-standards'; surveyId: string }

/**
 * Global AI chat context store. Pages `register()` a context on entry (enabling the footer
 * orb) and `clear()` it on leave. `open()`/`close()` drive the active page's drawer.
 */
/**
 * Where one context's remembered open/closed choice is kept (Task #2000).
 *
 * Per context, not global: a different survey — or a different AI surface — starts closed, which
 * is the behaviour the register() guards below already promise and the store's tests pin.
 */
function storageKey(ctx: AiChatContext): string {
  return `sc.ai-chat.open.${ctx.kind}.${ctx.surveyId}`
}

/**
 * Reading and writing the choice both swallow their errors on purpose: a private window, or a
 * browser set to refuse site data, throws on access rather than returning nothing. A remembered
 * panel position is not worth a broken page, so the fallback is simply "closed".
 */
function rememberChoice(ctx: AiChatContext | null, wantsOpen: boolean): void {
  if (ctx === null) return
  try {
    localStorage.setItem(storageKey(ctx), wantsOpen ? '1' : '0')
  } catch {
    /* storage unavailable — the choice is simply not remembered */
  }
}

function wasLeftOpen(ctx: AiChatContext): boolean {
  try {
    return localStorage.getItem(storageKey(ctx)) === '1'
  } catch {
    return false
  }
}

export const useAiChatContextStore = defineStore('ai-chat-context', () => {
  const context = ref<AiChatContext | null>(null)
  const isOpen = ref(false)

  /** The orb is visible whenever a page has registered a context. */
  const isEnabled = computed(() => context.value !== null)

  function register(ctx: AiChatContext): void {
    const sameContext =
      context.value?.kind === ctx.kind &&
      (context.value as { surveyId?: string } | null)?.surveyId ===
        (ctx as { surveyId?: string }).surveyId
    context.value = ctx
    // A freshly-registered *different* context must never inherit a previous page's open
    // drawer — but re-registering the SAME context must NOT close an open drawer. The
    // survey-builder page's watch re-runs register() on every currentSurvey change, and a
    // silent refreshSurvey (after applying an AI action, #1046) changes currentSurvey; without
    // this guard that re-register would force-close the open chat drawer. (#1046)
    // Arriving at a context for the first time this page-load restores what the user last chose
    // for it — closed unless they had left it open (Task #2000). Re-registering the SAME context
    // still leaves an open drawer alone, per the #1046 guard above.
    if (!sameContext) isOpen.value = wasLeftOpen(ctx)
  }

  /**
   * The page left. This closes the drawer WITHOUT recording a choice — unmounting is navigation,
   * not a decision, and treating it as one is what used to lose the user's chat on every return.
   */
  function clear(): void {
    context.value = null
    isOpen.value = false
  }

  function open(): void {
    isOpen.value = true
    rememberChoice(context.value, true)
  }

  function close(): void {
    isOpen.value = false
    rememberChoice(context.value, false)
  }

  return {
    context,
    isOpen,
    isEnabled,
    register,
    clear,
    open,
    close,
  }
})
